import { Avatar, Button, CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { UserService } from '../../Service/UserService';
import { setAuthUser } from '../../store/user/user-slice';
import { PhoneNrInput, InputKit } from '../UI';
import { createNotification } from '../../store/notifications/notification-slice';
import { zodResolver } from '@hookform/resolvers/zod';
import { generalInformationSchema } from './validations';

export interface IFormInput {
  name: string;
  lastName: string;
  email: string;
  profile: string;
  phone: string | null;
}

const GeneralInformation = () => {
  const [isLoading, setIsLoading] = useState({
    removeImageLoader: false,
    uploadImageLoader: false,
    formUpdateLoader: false,
  });

  const authUser = useAppSelector(state => state.user.user);

  const methods = useForm<IFormInput>({
    defaultValues: {
      name: authUser?.name,
      lastName: authUser?.lastName,
      email: authUser?.email,
      phone: authUser?.phone!,
    },
    resolver: zodResolver(generalInformationSchema),
  });
  const appDispatch = useAppDispatch();

  const updateUser = async (values: IFormInput) => {
    setIsLoading({ ...isLoading, formUpdateLoader: true });
    try {
      const user = await UserService.updateUser(values);
      appDispatch(setAuthUser(user?.data));
      appDispatch(
        createNotification({
          message: 'User updated successfully',
          type: 'success',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          message: 'Something went wrong',
          type: 'error',
        })
      );
    } finally {
      setIsLoading({ ...isLoading, formUpdateLoader: false });
    }
  };

  const onUploadImageOrRemove = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    setIsLoading({ ...isLoading, uploadImageLoader: true });
    try {
      const file = e.target.files![0];

      const formData = new FormData();
      formData.append('file', file);

      const user = await UserService.uploadProfile(formData);
      appDispatch(setAuthUser(user?.data));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading({ ...isLoading, uploadImageLoader: false });
    }
  };

  const onRemoveImage = async () => {
    setIsLoading({ ...isLoading, removeImageLoader: true });
    try {
      const user = await UserService.updateUser({
        profile: null!,
      });
      appDispatch(setAuthUser(user?.data));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading({ ...isLoading, removeImageLoader: false });
    }
  };

  return (
    <FormProvider {...methods}>
      <Box display={'flex'} gap={'32px'} alignItems={'center'}>
        <Avatar
          sx={{
            width: '160px',
            height: '160px',
          }}
          src={authUser?.profile}
          alt={authUser?.name}
        />

        <Box display={'flex'} flexDirection={'column'} gap={'12px'}>
          <Button
            sx={{
              background: '#3293DB',
              color: '#fff',
              paddingX: ' 42px ',
              fontSize: '14px',
              fontWeight: '500',
            }}
            variant='contained'
            component='label'
            startIcon={
              isLoading.uploadImageLoader && (
                <CircularProgress size={14} color='inherit' />
              )
            }
          >
            Change{' '}
            <input
              onChange={onUploadImageOrRemove}
              hidden
              accept='image/*'
              type='file'
            />
          </Button>
          <Button
            sx={{
              paddingX: '42px ',
              fontSize: '14px',
              fontWeight: '500',
              color: '#655C68',
              border: '1px solid #655C68',
              '&:hover': {
                border: '1px solid #655C68',
              },
            }}
            onClick={onRemoveImage}
            variant='outlined'
            startIcon={
              isLoading.removeImageLoader && (
                <CircularProgress size={14} color='inherit' />
              )
            }
          >
            Remove
          </Button>
        </Box>
      </Box>
      <form
        action='submit'
        style={{
          maxWidth: '425px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        onSubmit={methods.handleSubmit(updateUser)}
      >
        <Box mt={'24px'} display={'flex'} gap={'24px'}>
          <InputKit name='name' placeholder='First name' />
          <InputKit name='lastName' placeholder='Last name' />
        </Box>
        <InputKit
          name='email'
          placeholder='Email'
          type='email'
          sxStyle={{
            m: '24px 0',
            width: '100%',
          }}
          disabled
        />
        <PhoneNrInput name='phone' />
        <Box
          display={'flex'}
          justifyContent={'flex-end'}
          mt={'32px'}
          gap={'12px'}
        >
          <Button
            sx={{
              fontSize: '14px',
              color: '#655C68',
              border: '1px solid #655C68',

              '&:hover': {
                border: '1px solid #655C68',
              },
            }}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            sx={{
              background: '#3293DB',
              color: '#fff',
              boxShadow: 'unset',
              fontSize: '14px',
            }}
            type='submit'
            variant='contained'
            startIcon={
              isLoading.formUpdateLoader && (
                <CircularProgress size={14} color='inherit' />
              )
            }
          >
            Save Changes
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};

export default GeneralInformation;
